import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import ConfirmationDialog from 'components/global/ConfirmationDialog';
import Tag from 'components/global/Tag';
import Typography from 'components/global/Typography';

import useUpdateEffect from 'hooks/useUpdateEffect';

import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faTimes } from '@fortawesome/pro-solid-svg-icons';

import TableFilterTypes from '../Filters/TableFilterTypes';
import * as S from '../Table.styled';

const TableFilters = (props) => {
	const { table, columnFilters, setColumnFilters, storageKey } = props;

	const { t } = useTranslation();

	const [trashButtonRef, setTrashButtonRef] = useState(null);

	const convertFilterValueToString = useCallback(
		({ filterFn, value }) => {
			switch (filterFn) {
				case TableFilterTypes.multiSelection:
					return value?.map((item) => t(item)).join(', ');

				case TableFilterTypes.float:
					return `${value[0] ?? ''} - ${value[1] ?? ''}`;

				case TableFilterTypes.rating:
					return value.toFixed(1);

				default:
					return t(value);
			}
		},
		[t]
	);

	useEffect(() => {
		if (!storageKey) return;

		const storedFilters = window.localStorage.getItem(`${storageKey}_table_filters`);

		if (!storedFilters) return;

		const parsedFilters = JSON.parse(storedFilters);

		const fixedFilters = parsedFilters.filter((filter) => {
			const column = table.getColumn(filter.id);
			return column.columnDef.filterFn;
		});

		if (JSON.stringify(fixedFilters) !== JSON.stringify(parsedFilters)) {
			window.localStorage.setItem(`${storageKey}_table_filters`, JSON.stringify(columnFilters));
			setColumnFilters(fixedFilters);
		} else {
			setColumnFilters(parsedFilters);
		}
	}, []);

	useUpdateEffect(() => {
		if (!storageKey) return;

		const storedFilters = window.localStorage.getItem(`${storageKey}_table_filters`);

		// If there are no filters stored and there are filters to store
		if (!storedFilters && columnFilters.length > 0) {
			window.localStorage.setItem(`${storageKey}_table_filters`, JSON.stringify(columnFilters));
			return;
		}

		// If there are stored filters and there are no filters to store
		if (columnFilters.length === 0) {
			window.localStorage.removeItem(`${storageKey}_table_filters`);
			return;
		}

		// If there are stored filters and there are filters to store
		const parsedFilters = JSON.parse(storedFilters);

		if (JSON.stringify(parsedFilters) !== JSON.stringify(columnFilters)) {
			window.localStorage.setItem(`${storageKey}_table_filters`, JSON.stringify(columnFilters));
		}
	}, [columnFilters]);

	return (
		<>
			{columnFilters.length > 0 && (
				<S.FilterContainer className="table-filters-container">
					{columnFilters.map((filter, index) => {
						const column = table.getColumn(filter.id);
						const filterFn = column.columnDef.filterFn;

						return (
							<Tag
								key={index}
								onClose={(e) => {
									e.stopPropagation();
									setColumnFilters(columnFilters.filter((_, i) => i !== index));
								}}>
								<Typography fontWeight="700">{t(filter.id)}</Typography>
								<Typography fontWeight="700">
									{`filtered.${
										filterFn === TableFilterTypes.multiSelection
											? 'is'
											: filterFn === TableFilterTypes.float
												? 'between'
												: filterFn === TableFilterTypes.rating
													? filter.value === 5
														? 'rating5'
														: 'rating'
													: ''
									}`}
								</Typography>
								<S.FilterValue>
									<Typography>
										{convertFilterValueToString({ filterFn, value: filter.value })}
									</Typography>
								</S.FilterValue>
							</Tag>
						);
					})}

					<S.RemoveFilterContainer>
						<Typography color="var(--content-2)">
							<Trans
								i18nKey="filtersApplied"
								values={{
									count: columnFilters.length
								}}
							/>
						</Typography>

						<Button
							ref={setTrashButtonRef}
							variant="blueBorderless"
							label="clearFilters"
							size="small"
							css={S.RemoveFilterButtonCss}
						/>
					</S.RemoveFilterContainer>
					<ConfirmationDialog
						buttonRef={trashButtonRef}
						title="filter.deleteConfirmationTitle"
						subTitle="filter.deleteConfirmationSubtitle"
						icon={faTrash}
						rightLabel="yes"
						rightIcon={faCheck}
						rightAction={() => setColumnFilters([])}
						leftLabel="cancel"
						leftIcon={faTimes}
					/>
				</S.FilterContainer>
			)}
		</>
	);
};

export default TableFilters;
